import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import ShowOnRole from '../../main/ShowOnRole/ShowOnRole';
import DataTableNew from '../../main/DataTableNew/DataTableNew';
import NewClassificationDialog from '../NewClassificationDialog/NewClassificationDialog';
import DeleteClassificationsDialog from '../DeleteClassificationsDialog/DeleteClassificationsDialog';
import { ClassificationSortInput, UserRoleEnum, useGetClassificationTypeQuery, useGetClassificationListWithTypeQuery, useCreateClassificationMutation } from '../../../generated/graphql';
import { gql, useApolloClient } from '@apollo/client';
import { Plus, Trash2, Layers, Link2, Check, ClipboardPaste, X, AlertTriangle } from 'lucide-react';
import RefreshButton from '../../main/RefreshButton/RefreshButton';
import SearchBoxNew from '../../main/SearchBoxNew/SearchBoxNew';
import { createGraphQLOrder, TableSort } from '../../main/DataTableNew/DataTableSorting';

type PastedRow = {
    name: string;
    description: string;
};

type ClassificationDetailDialogProps = {
    visible: boolean;
    classificationTypeId: string;
    onClose: () => void;
};

const ClassificationDetailDialog = (props: ClassificationDetailDialogProps) => {
    const { visible, classificationTypeId, onClose } = props;

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchEntities, setSearchEntities] = useState("");
    const [clientSearchTerms, setClientSearchTerms] = useState<string[]>([]);
    const [newEntityVisible, setNewEntityVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [deleteAllVisible, setDeleteAllVisible] = useState(false);
    const [allIds, setAllIds] = useState<string[]>([]);
    const [linkCopied, setLinkCopied] = useState(false);
    const [pastedRows, setPastedRows] = useState<PastedRow[]>([]);
    const [isPasting, setIsPasting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);
    const [classificationCount, setClassificationCount] = useState<number | null>(null);
    const [createClassification] = useCreateClassificationMutation();
    const apolloClient = useApolloClient();
    const fetchingMore = useRef(false);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const fetchExistingNames = useCallback(async (): Promise<Set<string>> => {
        const ALL_CLASSIFICATION_NAMES = gql`
            query getAllClassificationNames($typeId: String) {
                ClassificationList(first: 10000, where: { typeId: { eq: $typeId } }) {
                    nodes {
                        name
                    }
                }
            }
        `;
        const { data } = await apolloClient.query<{
            ClassificationList?: { nodes?: Array<{ name?: string | null } | null> | null };
        }>({
            query: ALL_CLASSIFICATION_NAMES,
            variables: { typeId: classificationTypeId },
            fetchPolicy: 'network-only',
        });
        return new Set(
            (data?.ClassificationList?.nodes || [])
                .filter(Boolean)
                .map(n => n!.name?.toLowerCase() || '')
        );
    }, [apolloClient, classificationTypeId]);

    const CLASSIFICATION_COUNT_QUERY = gql`
        query getClassificationCount($typeId: String) {
            ClassificationList(first: 10000, where: { typeId: { eq: $typeId } }) {
                nodes { id }
            }
        }
    `;

    const fetchClassificationCount = useCallback(async () => {
        const { data } = await apolloClient.query<{
            ClassificationList?: { nodes?: Array<any> | null };
        }>({
            query: CLASSIFICATION_COUNT_QUERY,
            variables: { typeId: classificationTypeId },
            fetchPolicy: 'network-only',
        });
        setClassificationCount(data?.ClassificationList?.nodes?.length ?? 0);
    }, [apolloClient, classificationTypeId, CLASSIFICATION_COUNT_QUERY]);

    useEffect(() => {
        if (visible) fetchClassificationCount();
    }, [visible, fetchClassificationCount]);

    const classificationType = useGetClassificationTypeQuery({
        variables: { id: classificationTypeId }
    });

    const classifications = useGetClassificationListWithTypeQuery({
        variables: {
            typeId: classificationTypeId,
            contains: searchEntities,
            order: createGraphQLOrder<ClassificationSortInput>(sort),
        }
    });

    const filteredClassifications = useMemo(() => {
        const nodes = classifications.data?.ClassificationList?.nodes;
        if (!nodes || clientSearchTerms.length === 0) return nodes;
        return nodes.filter(node => {
            if (!node) return false;
            const target = `${node.name || ''} ${(node as any).description || ''}`.toLowerCase();
            return clientSearchTerms.every(term => target.includes(term.toLowerCase()));
        });
    }, [classifications.data, clientSearchTerms]);

    const handleOnClose = () => {
        setNewEntityVisible(false);
        classifications.refetch();
        classificationType.refetch();
        fetchClassificationCount();
    };

    const handleCopyLink = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('detail', classificationTypeId);
        navigator.clipboard.writeText(url.toString());
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) return;

            const lines = text.trim().split('\n');
            const headings = ['name', 'description', 'title', 'classification', 'type'];
            const parsedRows: PastedRow[] = lines
                .map(line => {
                    const parts = line.split('\t');
                    return {
                        name: (parts[0] || '').trim(),
                        description: (parts[1] || '').trim(),
                    };
                })
                .filter(r => {
                    if (!r.name) return false;
                    const nameLower = r.name.toLowerCase();
                    const descLower = r.description.toLowerCase();
                    if (headings.includes(nameLower) && (headings.includes(descLower) || !descLower)) return false;
                    return true;
                });

            if (parsedRows.length === 0) return;

            // Deduplicate within pasted data (keep first occurrence, case-insensitive)
            const seenNames = new Set<string>();
            const uniqueRows = parsedRows.filter(r => {
                const key = r.name.toLowerCase();
                if (seenNames.has(key)) return false;
                seenNames.add(key);
                return true;
            });

            // Fetch all existing classifications to check for duplicates
            const existingNames = await fetchExistingNames();

            const rows = uniqueRows.filter(r => !existingNames.has(r.name.toLowerCase()));
            const removed = parsedRows.length - rows.length;

            setDuplicatesRemoved(removed);
            if (rows.length > 0) {
                setPastedRows(rows);
            }
        } catch {
            // Clipboard access denied
        }
    };

    const handleDeletePastedRow = (index: number) => {
        setPastedRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleImportPasted = async () => {
        if (pastedRows.length === 0) return;
        setIsPasting(true);
        setImportProgress(0);
        try {
            // Re-check for duplicates at import time
            const existingNames = await fetchExistingNames();
            const rowsToImport = pastedRows.filter(r => !existingNames.has(r.name.toLowerCase()));

            if (rowsToImport.length === 0) {
                setPastedRows([]);
                return;
            }

            const batchSize = 5;
            let completed = 0;
            for (let i = 0; i < rowsToImport.length; i += batchSize) {
                const batch = rowsToImport.slice(i, i + batchSize);
                await Promise.all(
                    batch.map(row =>
                        createClassification({
                            variables: {
                                classification: {
                                    name: row.name,
                                    description: row.description,
                                    typeId: classificationTypeId,
                                }
                            }
                        })
                    )
                );
                completed += batch.length;
                setImportProgress(Math.round((completed / rowsToImport.length) * 100));
            }
            setPastedRows([]);
            setDuplicatesRemoved(0);
            classifications.refetch();
            fetchClassificationCount();
        } finally {
            setIsPasting(false);
            setImportProgress(0);
        }
    };

    const handleDeleteAll = async () => {
        const ALL_CLASSIFICATION_IDS = gql`
            query getAllClassificationIds($typeId: String) {
                ClassificationList(first: 10000, where: { typeId: { eq: $typeId } }) {
                    nodes {
                        id
                    }
                }
            }
        `;
        const { data } = await apolloClient.query<{
            ClassificationList?: { nodes?: Array<{ id?: string | null } | null> | null };
        }>({
            query: ALL_CLASSIFICATION_IDS,
            variables: { typeId: classificationTypeId },
            fetchPolicy: 'network-only',
        });
        const ids = (data?.ClassificationList?.nodes || [])
            .filter(Boolean)
            .map(n => n!.id!)
            .filter(Boolean);
        if (ids.length > 0) {
            setAllIds(ids);
            setDeleteAllVisible(true);
        }
    };

    const rowsWithEmptyName = pastedRows.filter(r => !r.name.trim()).length;
    const rowsWithEmptyDesc = pastedRows.filter(r => !r.description.trim()).length;

    return (
        <DialogBase
            title={
                <span className="flex items-center gap-2.5">
                    <span
                        className="flex items-center justify-center rounded-lg"
                        style={{ width: 30, height: 30, backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                        <Layers size={16} />
                    </span>
                    <span style={{ color: 'var(--text-primary)' }}>{classificationType.data?.ClassificationType?.name || "Classification"}</span>
                </span>
            }
            visible={visible}
            type="Custom"
            onClose={onClose}
            width="800px"
            isLoading={classificationType.loading && !classificationType.data}
            headerActions={
                <button
                    onClick={handleCopyLink}
                    className="p-1.5 rounded-lg transition-colors duration-150"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: linkCopied ? 'var(--accent)' : 'var(--text-tertiary)' }}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    aria-label="Copy link"
                    title="Copy link to clipboard"
                >
                    {linkCopied ? <Check size={16} /> : <Link2 size={16} />}
                </button>
            }
        >
            <div className="flex flex-col overflow-hidden" style={{ height: 'calc(85vh - 180px)', margin: '-20px -24px' }}>
                {/* Frozen toolbar */}
                <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                        {classificationCount != null
                            ? `${classificationCount.toLocaleString()} item${classificationCount !== 1 ? 's' : ''}`
                            : ''}
                    </span>
                    <div className="flex-1" />
                    <RefreshButton onRefresh={() => { classifications.refetch(); classificationType.refetch(); }} className="btn-ghost" iconSize={16} showLabel={false} />
                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                        {selectedIds.length > 0 && (
                            <button className="btn-danger" onClick={() => setDeleteVisible(true)}>
                                <Trash2 size={16} /> Delete ({selectedIds.length})
                            </button>
                        )}
                        <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger, #dc2626)' }} onClick={handleDeleteAll}>
                            <Trash2 size={15} /> Delete All
                        </button>
                        <button className="btn btn-ghost text-sm" onClick={handlePasteFromClipboard}>
                            <ClipboardPaste size={15} /> Paste from Clipboard
                        </button>
                        <button className="btn-primary" onClick={() => setNewEntityVisible(true)}>
                            <Plus size={16} /> Add Classification
                        </button>
                    </ShowOnRole>
                </div>

                {/* Paste preview */}
                {pastedRows.length > 0 && (
                    <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--border-default)' }}>
                        <div className="flex flex-col" style={{ backgroundColor: 'var(--surface-50)' }}>
                            {isPasting && (
                                <div style={{ height: '3px', width: '100%', backgroundColor: 'var(--border-default)' }}>
                                    <div style={{
                                        height: '100%',
                                        width: `${importProgress}%`,
                                        backgroundColor: 'var(--accent)',
                                        transition: 'width 0.2s ease',
                                        borderRadius: '0 2px 2px 0',
                                    }} />
                                </div>
                            )}
                            <div className="flex items-center justify-between px-4 py-2">
                            <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                                {isPasting
                                    ? `Importing… ${importProgress}%`
                                    : `${pastedRows.length.toLocaleString()} record${pastedRows.length !== 1 ? 's' : ''} to import`
                                }
                            </span>
                            <div className="flex items-center gap-2">
                                <button className="btn btn-ghost text-xs" style={{ color: 'var(--text-tertiary)' }} onClick={() => { setPastedRows([]); setDuplicatesRemoved(0); }}>
                                    <Trash2 size={13} /> Clear
                                </button>
                                <button
                                    className="btn-primary text-xs"
                                    style={{ padding: '4px 12px', fontSize: '0.78rem' }}
                                    onClick={handleImportPasted}
                                    disabled={isPasting}
                                >
                                    {isPasting ? 'Importing…' : 'Import All'}
                                </button>
                            </div>
                            </div>
                        </div>
                        {(rowsWithEmptyName > 0 || rowsWithEmptyDesc > 0) && (
                            <div className="flex items-center gap-2 px-4 py-1.5 text-xs" style={{ color: 'var(--warning, #d97706)', backgroundColor: 'var(--warning-soft, rgba(217,119,6,0.08))' }}>
                                <AlertTriangle size={13} />
                                <span>
                                    {rowsWithEmptyName > 0 && `${rowsWithEmptyName} row${rowsWithEmptyName !== 1 ? 's' : ''} with empty name`}
                                    {rowsWithEmptyName > 0 && rowsWithEmptyDesc > 0 && ', '}
                                    {rowsWithEmptyDesc > 0 && `${rowsWithEmptyDesc} row${rowsWithEmptyDesc !== 1 ? 's' : ''} with empty description`}
                                </span>
                            </div>
                        )}
                        {duplicatesRemoved > 0 && (
                            <div className="flex items-center gap-2 px-4 py-1.5 text-xs" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--surface-100, rgba(0,0,0,0.03))' }}>
                                <AlertTriangle size={13} />
                                <span>{duplicatesRemoved} duplicate{duplicatesRemoved !== 1 ? 's' : ''} removed</span>
                            </div>
                        )}
                        <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                <thead>
                                    <tr style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'var(--surface-50)' }}>
                                        <th style={{ padding: '5px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-default)', width: '36px' }}>#</th>
                                        <th style={{ padding: '5px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-default)' }}>Name</th>
                                        <th style={{ padding: '5px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-default)' }}>Description</th>
                                        <th style={{ padding: '5px 12px', width: '32px', borderBottom: '1px solid var(--border-default)' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pastedRows.slice(0, 100).map((row, i) => (
                                        <tr key={i} style={{ backgroundColor: (!row.name.trim() || !row.description.trim()) ? 'var(--warning-soft, rgba(217,119,6,0.05))' : undefined }}>
                                            <td style={{ padding: '4px 12px', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-default)' }}>{i + 1}</td>
                                            <td style={{ padding: '4px 12px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)' }}>
                                                {row.name || <span style={{ color: 'var(--warning, #d97706)', fontStyle: 'italic' }}>empty</span>}
                                            </td>
                                            <td style={{ padding: '4px 12px', color: 'var(--text-primary)', borderBottom: '1px solid var(--border-default)' }}>
                                                {row.description || <span style={{ color: 'var(--warning, #d97706)', fontStyle: 'italic' }}>empty</span>}
                                            </td>
                                            <td style={{ padding: '4px 12px', textAlign: 'center', borderBottom: '1px solid var(--border-default)' }}>
                                                <button
                                                    onClick={() => handleDeletePastedRow(i)}
                                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: '2px', borderRadius: '4px', display: 'inline-flex' }}
                                                    title="Remove row"
                                                >
                                                    <X size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {pastedRows.length > 100 && (
                                        <tr>
                                            <td colSpan={4} style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                                                … and {(pastedRows.length - 100).toLocaleString()} more
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Frozen search */}
                <div className="flex-shrink-0">
                    <SearchBoxNew
                        onSearchChanged={value => { setSearchEntities(value); setClientSearchTerms([]); }}
                        onClientSearchTermsChanged={terms => setClientSearchTerms(terms)}
                        controlledValue={searchEntities}
                    />
                </div>

                {/* Scrollable table rows only */}
                <div className="flex-1 min-h-0 overflow-auto">
                    <DataTableNew
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        isLoading={classifications.loading}
                        data={filteredClassifications}
                        showDescription
                        searchable={false}
                        onRowClick={(row) => setSearchEntities(row.name || '')}
                        onSelected={(ids) => setSelectedIds(ids)}
                        fetchMore={() => {
                            if (fetchingMore.current) return;
                            if (!classifications.data?.ClassificationList?.pageInfo?.hasNextPage) return;
                            fetchingMore.current = true;
                            classifications.fetchMore({
                                variables: {
                                    contains: searchEntities,
                                    after: classifications.data?.ClassificationList?.pageInfo.endCursor,
                                    order: createGraphQLOrder<ClassificationSortInput>(sort),
                                },
                                updateQuery: (previousResult, { fetchMoreResult }) => {
                                    fetchingMore.current = false;
                                    if (!fetchMoreResult) return previousResult;
                                    const previousNodes = previousResult.ClassificationList?.nodes || [];
                                    const newNodes = fetchMoreResult.ClassificationList?.nodes || [];
                                    return {
                                        ...previousResult,
                                        ClassificationList: {
                                            nodes: [...previousNodes, ...newNodes],
                                            pageInfo: fetchMoreResult.ClassificationList?.pageInfo as any
                                        }
                                    };
                                }
                            }).catch(() => { fetchingMore.current = false; });
                        }}
                    />
                </div>
            </div>

            {newEntityVisible && <NewClassificationDialog visible={newEntityVisible} onClose={handleOnClose} typeId={classificationTypeId} />}
            {deleteVisible && <DeleteClassificationsDialog visible={deleteVisible} ids={selectedIds} onClose={() => { setDeleteVisible(false); setSelectedIds([]); classifications.refetch(); fetchClassificationCount(); }} />}
            {deleteAllVisible && <DeleteClassificationsDialog visible={deleteAllVisible} ids={allIds} onClose={() => { setDeleteAllVisible(false); setAllIds([]); setSelectedIds([]); classifications.refetch(); fetchClassificationCount(); }} />}
        </DialogBase>
    );
};

export default ClassificationDetailDialog;
